import { useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/store/hooks';
import {
  uploadAttachment,
  fetchAttachments,
  clearAttachments,
} from '../../redux/features/attachmentsSlice';
import { AttachmentList } from './AttachmentList';
import { AttachmentEntityType } from '../../services/httpServices/attachmentService';
import { X, Upload, FileText, Loader2, Send } from 'lucide-react';

interface DiagnosticSubmitModalProps {
  entityType: AttachmentEntityType;
  entityId: string;
  entityName: string;
  onClose: () => void;
}

export function DiagnosticSubmitModal({ entityType, entityId, entityName, onClose }: DiagnosticSubmitModalProps) {
  const dispatch = useAppDispatch();
  const { uploading } = useAppSelector((state) => state.attachments);
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    await dispatch(uploadAttachment({
      file,
      entityType,
      entityId,
      description: description || undefined,
    }));
    setFile(null);
    setDescription('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 2000);
    // Refresh attachments list
    dispatch(fetchAttachments({ entityType, entityId }));
  };

  const handleClose = () => {
    dispatch(clearAttachments());
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-surface rounded-xl border border-border w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-surface z-10 flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Send size={18} className="text-primary" />
            <h2 className="text-lg font-semibold text-text">
              Submit Doc & Data
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg hover:bg-background text-text-muted transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <p className="text-sm text-text-muted">
            Submit documents and data for <span className="font-medium text-text">{entityName}</span>
          </p>

          {/* File Upload */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-text">Upload Document</label>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                className="hidden"
                id="diagnostic-file-upload"
              />
              <label
                htmlFor="diagnostic-file-upload"
                className="flex-1 flex items-center gap-2 px-3 py-2 border border-border rounded-lg bg-background text-text text-sm cursor-pointer hover:border-primary transition-colors"
              >
                <FileText size={16} className="text-text-muted" />
                <span className="truncate">{file ? file.name : 'Choose a file...'}</span>
              </label>
              <button
                onClick={handleUpload}
                disabled={!file || uploading}
                className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                Upload
              </button>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-text">Description / Notes</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Enter any notes or description about the submitted data..."
            />
          </div>

          {submitted && (
            <div className="p-3 bg-success/10 text-success rounded-lg text-sm flex items-center gap-2">
              <Send size={14} />
              Document uploaded successfully!
            </div>
          )}

          {/* Existing Attachments */}
          <div className="space-y-2 pt-2 border-t border-border">
            <h3 className="text-sm font-medium text-text">Submitted Documents</h3>
            <AttachmentList entityType={entityType} entityId={entityId} allowDelete={true} />
          </div>
        </div>

        <div className="sticky bottom-0 bg-surface z-10 flex items-center justify-end gap-2 p-4 border-t border-border">
          <button
            onClick={handleClose}
            className="px-4 py-2 border border-border rounded-lg hover:bg-background transition-colors text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
